import React, { useEffect, useMemo, useState } from "react";
import Header from "../../header/header";
import { Button, Checkbox, InputAdornment, LinearProgress, Pagination, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import { Chat } from "../../models";
import { getChats, updateChat } from "../../api";
import { searchService } from "../../scripts/search.service";
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useSnackbar } from "notistack";
import { ChatTableRow } from "../components/ChatTableRow";

const itemsPerPage = 15;

export const ChatListPage: React.FC = () => {
  const user = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();

  // Data state
  const [chats, setChats] = useState<Chat[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  /// Search state (filtering & pagination)
  const [restore, setRestore] = useState(false);
  const [query, setQuery] = useState<string>(searchService.currentSearchValue?.query ?? '');
  const [page, setPage] = useState(searchService.currentSearchValue?.page ?? 1);
  const [selectedChatIds, setSelectedChatIds] = useState<number[]>([]);

  const searchChats = useMemo(() => chats?.filter(chat => {
    const queryMatch = chat.name.toLowerCase().includes(query.toLowerCase());

    return queryMatch && (restore ? chat.status === 'deleted' : chat.status !== 'deleted');
  }), [chats, query, restore]);

  const selectedChats = useMemo(() => searchChats?.filter(chat => selectedChatIds.includes(chat.id)), [searchChats, selectedChatIds]);
  const allSelected = useMemo(() => selectedChats?.length === searchChats?.length && (searchChats?.length ?? 0) > 0, [selectedChats, searchChats]);

  const paginatedChats = useMemo(() => searchChats?.slice((page - 1) * itemsPerPage, page * itemsPerPage), [searchChats, page]);

  // Derived state
  const loading = useMemo(() => !chats && !error, [chats, error]);

  // Save search state to local storage
  useEffect(() => searchService.setSearch({
    query,
    types: searchService.currentSearchValue?.types,
    page,
  }), [query, page]);

  // Reset page when query or types change
  useEffect(() => {
    setPage(1);
  }, [query]);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setError(null);

    try {
      const chats = await getChats();
      setChats(chats);
    } catch (error) {
      setError(error as string);
    }
  }

  function handleChatSelect(chat: Chat) {
    if (selectedChatIds.includes(chat.id)) {
      setSelectedChatIds(selectedChatIds.filter(id => id !== chat.id));
    } else {
      setSelectedChatIds([...selectedChatIds, chat.id]);
    }
  }

  function handleSelectAll() {
    if (allSelected) {
      setSelectedChatIds([]);
    } else {
      setSelectedChatIds(searchChats?.map(chat => chat.id) ?? []);
    }
  }

  async function handleDeleteSelectedChats() {
    enqueueSnackbar("Deleting selected chats", { variant: "info" });

    try {
      const selectedChats = searchChats?.filter(persona => selectedChatIds.includes(persona.id)) ?? [];
      await Promise.all(selectedChats.map(persona => updateChat(persona.id, {
        status: "deleted"
      })));
      enqueueSnackbar("Selected chats deleted", { variant: "success" });
      setSelectedChatIds([]);
      await fetchData();
    } catch (e) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
      console.error(e)
    }
  }

  async function handleRestoreSelectedChats() {
    enqueueSnackbar("Restoring selected chats", { variant: "info" });

    try {
      const selectedChats = searchChats?.filter(persona => selectedChatIds.includes(persona.id)) ?? [];
      await Promise.all(selectedChats.map(persona => updateChat(persona.id, {
        status: "active"
      })));
      enqueueSnackbar("Selected chats restored", { variant: "success" });
      setSelectedChatIds([]);
      await fetchData();
    } catch (e) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
      console.error(e)
    }
  }

  return (
    <>
      <Header />
      {!loading && !error && <div className="content-header" style={{ top: "0.5rem", width: "90%", left: "6.25rem" }}>
        <div className="left" style={{ display: "flex" }}>
          <TextField
            label="Search"
            style={{ marginRight: "10px" }}
            onChange={(e) => setQuery(e.target.value)}
            focused
            size="small"
            value={query}
            className="foreInputWhite"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className="right" style={{ display: "flex" }}>
          <Link to={`/content/chats/new`}><Button style={{ marginRight: "10px" }} variant="contained" color="primary" className="spacer">New</Button></Link>
          {restore
            ? <Button variant="contained" color="primary" onClick={() => setRestore(false)} className="spacer">Normal</Button>
            : <Button disabled={!user?.perms.c3} variant="contained" color="primary" onClick={() => setRestore(true)} className="spacer">Restore</Button>
          }
          <Link to="/"><Button style={{ marginLeft: "10px" }} variant="contained" color="primary" className="spacer">Back</Button></Link>
        </div>
      </div>}
      {loading && <div style={{ width: "50%" }}>
        <LinearProgress />
      </div>}
      {error && <div>Error: {error}</div>}
      {!loading && !error && <div className="profiles-container" style={{ width: "70%", top: "1rem", maxHeight: "80vh" }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead className='table_head'>
              <TableRow>
                <TableCell className="table_cell top_row" style={{ width: "20%" }}>
                  <div style={{ display: "flex" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ fontWeight: "600" }}>Select All</span>
                      <Checkbox className="table_cell top_row" checked={allSelected} onChange={handleSelectAll}></Checkbox>
                    </div>
                    {(selectedChats?.length ?? 0) > 0 && (
                      <>
                        {restore ?
                          <Button variant="contained" color="secondary" size="small" onClick={handleRestoreSelectedChats} style={{ padding: "0.25rem", width: "58%", height: "90%", top: "2.5rem" }}>RESTORE SELECTED</Button> :
                          <Button variant="contained" color="secondary" size="small" onClick={handleDeleteSelectedChats} style={{ padding: "0.25rem", width: "58%", height: "90%", top: "2.5rem" }}>DELETE SELECTED</Button>
                        }
                      </>
                    )}
                  </div>


                </TableCell>
                {/* <TableCell className="table_cell top_row" style={{width: "10%"}}></TableCell> */}
                <TableCell className="table_cell top_row" style={{ fontWeight: "600", width: "45%" }}>Name</TableCell>
                <TableCell className="table_cell top_row" style={{ fontWeight: "600", width: "15%" }}>Date Created</TableCell>
                <TableCell className="table_cell top_row" style={{ fontWeight: "600" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedChats?.map((chat, index) => (
                <ChatTableRow
                  key={chat.id}
                  chat={chat}
                  selected={selectedChats?.includes(chat) ?? false}
                  dark={index % 2 === 0}
                  onChatSelect={handleChatSelect}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      }
      {!loading && !error && <div className="pagination-container">
        <Pagination color="secondary" count={Math.ceil((searchChats?.length ?? 0) / itemsPerPage)} page={page} onChange={(_, page) => setPage(page)} />
      </div>}
    </>
  );
};
